import {
    setSparkflowGlobalContext,
    SparkflowContextOptions,
} from '@thoughtspot/blink-context';
import { isTrialVersionEnabled } from '@thoughtspot/feature-service';
import { translate } from '@thoughtspot/i18n';
import React, { Suspense, useEffect } from 'react';
import { useAppContext } from '/@contexts/global-app-context';
import { useGetSpotbuildGeneratedIds } from '/@services/trial/trial-service';
import { isGlobalBannerVisible } from '../../routing/authenticated-app.util';
import { GlobalBanner } from './global-banners.container';
import { removeTypename } from './trial-info/trial-info.util';

/**
 * Reset the sparkflow global context when the user navigates to a different page
 * When in agent-selection screen and we manually route to different page, by
 * skipping the pinboard edit page then it has to be ensured that the global banner
 * and free trial banner shows again
 * Currently the isLoadingIndicatorVisible is to false if we go in pinboard page
 * in edit mode
 * Similarly if user is in pinboard page and then manually routes to different page,
 * then it has to be ensured that the global banner and free trial banner shows again
 * @param pathname - The pathname of the current page
 * @param isLoadingIndicatorVisible - Whether the loading indicator is visible
 * @param hideSparkflowBanner - Whether the global banner is hidden
 * @param updateSparkflowGlobalContext - The function to update the sparkflow global context
 */
export const resetSparkflowGlobalContext = (
    pathname: string,
    hideSparkflowBanner: boolean,
    isLoadingIndicatorVisible: boolean,
    updateSparkflowGlobalContext: (
        context: Partial<SparkflowContextOptions>,
    ) => void,
) => {
    if (
        !pathname.includes('/sparkflow/pinboard') &&
        (hideSparkflowBanner || isLoadingIndicatorVisible)
    ) {
        updateSparkflowGlobalContext({
            isLoadingIndicatorVisible: false,
            hideSparkflowBanner: false,
        });
    }
};
interface SparkflowGlobalBannerProps {
    pathname: string;
    isServiceWorkerEnabled: boolean;
}

const SparkflowGlobalBannerContainer: React.FC<SparkflowGlobalBannerProps> = ({
    pathname,
    isServiceWorkerEnabled,
}) => {
    const { sessionService } = useAppContext();
    const { data, error, loading } = useGetSpotbuildGeneratedIds();
    const { updateSparkflowGlobalContext } = setSparkflowGlobalContext();

    useEffect(() => {
        if (data && !loading) {
            const spotbuildGeneratedIdsDataWithoutTypename = removeTypename(
                data,
            );
            updateSparkflowGlobalContext({
                spotbuildGenResponse: spotbuildGeneratedIdsDataWithoutTypename,
                spotterVizStarterPrompt: translate(
                    'sparkflow.spotterViz.defaultprompt',
                    {
                        dataset:
                            spotbuildGeneratedIdsDataWithoutTypename[0]
                                ?.data_sources[0]?.model.name,
                        worksheetId:
                            spotbuildGeneratedIdsDataWithoutTypename[0]
                                ?.data_sources[0]?.model?.id,
                    },
                ),
            });
        }
    }, [data, loading, updateSparkflowGlobalContext]);

    const renderGlobalBanner = () => {
        return (
            <>
                {isGlobalBannerVisible(
                    pathname,
                    isTrialVersionEnabled(),
                    sessionService.isSlackBotEnabled(),
                    isServiceWorkerEnabled,
                ) ? (
                    <GlobalBanner />
                ) : null}
            </>
        );
    };

    return renderGlobalBanner();
};

export default SparkflowGlobalBannerContainer;
